import { Controller } from "@hotwired/stimulus"

// Mounts the Stripe Payment Element inside the "Add billing details" accordion
// section and confirms it before the checkout form is submitted.
//
// Why the Payment Element rather than Stripe's Embedded Checkout: the design
// drives the purchase from our own "Confirm Purchase" button in the summary
// rail, and Embedded Checkout renders its own submit button inside an iframe we
// can't drive. The Payment Element is the embed that fits — Stripe still hosts
// the sensitive fields, so card data never touches our origin.
//
// Flow: confirm the SetupIntent (answering any SCA challenge inline) -> put the
// resulting payment method id on the form -> submit. Pay-by-invoice skips all
// of it and submits directly, since there is no payment method to collect.
// With an intent URL instead of a client secret the Element mounts without an
// intent and one is created only on submit, so merely opening the page leaves
// nothing behind at Stripe.
export default class extends Controller {
  static targets = ["element", "errors", "paymentMethod", "paymentWaived", "submit"]
  static values = {
    publishableKey: String, clientSecret: String, intentUrl: String, paymentMethodTypes: Array,
    billingDetails: Object, returnUrl: String, genericError: String,
  }

  connect() {
    if (!(this.clientSecretValue || this.intentUrlValue) || typeof window.Stripe === "undefined") return

    const intent = this.clientSecretValue
      ? { clientSecret: this.clientSecretValue }
      : { mode: "setup", currency: "eur", paymentMethodTypes: this.paymentMethodTypesValue }

    this.stripe = window.Stripe(this.publishableKeyValue)
    this.elements = this.stripe.elements({
      ...intent,
      // Stripe otherwise follows the browser language, not the app locale.
      locale: document.documentElement.lang || "auto",
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: "#d1801f",
          colorText: "#1f3557",
          borderRadius: "6px",
          fontFamily: "Arial, Helvetica, sans-serif",
        },
      },
    })
    const billingDetails = this.billingDetailsValue
    // Link stays off: it recognises the browser, not the account, and offered another user's card.
    const options = { wallets: { link: "never" } }
    if (Object.keys(billingDetails).length) options.defaultValues = { billingDetails }
    this.paymentElement = this.elements.create("payment", options)
    this.mountWhenVisible()
  }

  // The element starts inside a collapsed accordion section (and, for a beat, under
  // Alpine's x-cloak). Stripe cannot lay out a Payment Element in a hidden or
  // zero-size container — it mounts blank and never recovers — so defer the mount
  // until the billing step is actually revealed. IntersectionObserver fires an
  // initial callback too, so an already-visible element mounts right away.
  mountWhenVisible() {
    const target = this.elementTarget.parentElement || this.elementTarget
    this.observer = new IntersectionObserver((entries) => {
      if (this.mounted || !entries.some((e) => e.isIntersecting)) return
      this.mounted = true
      this.paymentElement.mount(this.elementTarget)
      this.observer.disconnect()
      this.observer = null
    }, { threshold: 0.01 })
    this.observer.observe(target)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.paymentElement) this.paymentElement.destroy()
  }

  async submit(event) {
    // requestSubmit() below re-fires this same submit event. Once the card is
    // confirmed, fall straight through so the form actually posts — confirming
    // the (now succeeded) SetupIntent a second time would 400. The button is
    // already locked from the first pass.
    if (this.confirmed) return

    // Nothing to confirm (no Stripe, invoice or a waiving code): post natively, button locked against a double post (#59).
    if (!this.stripe || this.collectionMethod === "send_invoice" || this.paymentWaived) {
      this.setBusy(true)
      return
    }

    event.preventDefault()
    this.setBusy(true)
    this.clearError()

    // return_url is REQUIRED by Stripe whenever the Payment Element offers a
    // redirect-capable method (Link, SEPA…), even with redirect: "if_required".
    // A card confirms inline and never actually redirects, so it returns the
    // setupIntent here and we submit the form; a redirect method would instead
    // bounce to return_url (handled by the checkout success action).
    let result
    try {
      const confirm = { elements: this.elements, confirmParams: { return_url: this.returnUrlValue }, redirect: "if_required" }
      if (!this.clientSecretValue) {
        const { error: incomplete } = await this.elements.submit()
        if (incomplete) {
          this.setBusy(false)
          return
        }
        confirm.clientSecret = await this.createIntent()
      }

      result = await this.stripe.confirmSetup(confirm)
    } catch (e) {
      // A thrown error (network, Stripe.js not loaded) must not leave the
      // customer with a locked button and no message.
      this.showError(this.genericErrorValue || e.message)
      this.setBusy(false)
      return
    }

    const { error, setupIntent } = result
    if (error) {
      this.showError(error.message)
      this.setBusy(false)
      return
    }

    this.confirmed = true
    this.paymentMethodTarget.value = setupIntent.payment_method
    this.element.requestSubmit()
  }

  async createIntent() {
    const response = await fetch(this.intentUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
    })
    if (!response.ok) throw new Error(this.genericErrorValue)
    return (await response.json()).client_secret
  }

  get paymentWaived() {
    return this.hasPaymentWaivedTarget && this.paymentWaivedTarget.value === "1"
  }

  get collectionMethod() {
    const checked = this.element.querySelector("input[name='collection_method']:checked")
    return checked ? checked.value : "charge_automatically"
  }

  setBusy(busy) {
    const button = this.submitTarget
    button.disabled = busy
    button.classList.toggle("opacity-60", busy)
    button.classList.toggle("cursor-not-allowed", busy)
    button.setAttribute("aria-busy", busy ? "true" : "false")

    if (!button.dataset.idleLabel) button.dataset.idleLabel = button.textContent.trim()
    const busyLabel = button.dataset.busyLabel
    if (busyLabel) button.textContent = busy ? busyLabel : button.dataset.idleLabel
  }

  showError(message) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.textContent = message
    this.errorsTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.textContent = ""
    this.errorsTarget.classList.add("hidden")
  }
}
